const translations = {
  en: {
    // Navigation
    nav: {
      requestDemo: "Request Demo",
      backToHome: "Back to Home"
    },
    
    // Hero Section
    hero: {
      title: "KlinikTrack",
      subtitle: "Hospital Asset Tracking — Made Simple",
      tagline1: "Plug & play visibility for critical medical equipment.",
      tagline2: "Designed for hospital operations.",
      cta: "Request access to the demo platform"
    },
    
    // Problem Section
    problem: {
      title: "Hospitals lose time — not equipment.",
      description: "Every day, hospital staff waste valuable time searching for mobile equipment. Assets move between departments without visibility, leading to inefficiency and operational pressure.",
      bullet1: "Equipment is hard to locate across floors and services",
      bullet2: "Utilization is poorly understood",
      bullet3: "\"Just in case\" purchases become the norm",
      bullet4: "Operational stress increases"
    },
    
    // Solution Section
    solution: {
      title: "Operational visibility, without disruption.",
      description: "KlinikTrack provides hospitals with a simple way to locate and monitor critical assets by zone, floor, or department — without touching clinical workflows or patient data.",
      feature1Title: "Asset visibility by location",
      feature1Desc: "Know where your equipment is at any time.",
      feature2Title: "Simple alerts & status monitoring",
      feature2Desc: "Get notified when equipment status changes.",
      feature3Title: "Usage and availability insights",
      feature3Desc: "Understand how your assets are utilized.",
      closing: "Built for operations. Designed for hospitals."
    },
    
    // Track Section
    track: {
      title: "What can you track with KlinikTrack?",
      description: "KlinikTrack focuses on mobile, high-value and frequently misplaced hospital assets that are critical for daily operations.",
      items: {
        infusionPumps: "Infusion Pumps",
        beds: "Hospital Beds",
        wheelchairs: "Wheelchairs",
        biomedical: "Mobile Biomedical Equipment",
        carts: "Care & Service Carts",
        cables: "Cables & Accessories",
        medication: "Medication Boxes & Storage Containers"
      },
      moreText: "And more... Any mobile asset that moves between rooms, floors, or departments can be tracked.",
      disclaimer: "KlinikTrack tracks equipment and objects — not patients or staff. No personal or medical data is collected."
    },
    
    // Why Section
    why: {
      title: "Why KlinikTrack?",
      plugPlay: {
        title: "Plug & Play",
        desc: "Fast deployment, no heavy IT integration, reversible installation."
      },
      aiAssisted: {
        title: "AI-Assisted",
        desc: "Smart alerts and basic usage insights to support operational decisions — not automation."
      },
      resilient: {
        title: "Resilient by Design",
        desc: "Battery-powered devices, tolerant to partial connectivity, designed for hospital environments."
      },
      gdpr: {
        title: "GDPR & Privacy First",
        desc: "No patient data, no staff tracking, EU-hosted infrastructure."
      },
      operational: {
        title: "Operational, Not Clinical",
        desc: "Decision support for operations. No medical claims. No clinical responsibility."
      }
    },
    
    // Preview Section
    preview: {
      title: "A clear operational dashboard.",
      description: "KlinikTrack provides a simple, intuitive dashboard designed for hospital operations.",
      feature1: "Asset list & status",
      feature2: "Location and last seen",
      feature3: "Alerts and history"
    },
    
    // Demo Form
    form: {
      title: "Access the KlinikTrack demo platform",
      description: "Explore how hospitals can gain operational visibility in minutes.",
      successTitle: "Request Received",
      successMessage: "Thank you for your interest in KlinikTrack. We will review your request and get back to you shortly.",
      sections: {
        yourInfo: "Your Information",
        hospitalInfo: "Hospital Information",
        whatToTrack: "What do you want to track?",
        timeline: "Project Timeline",
        additional: "Additional Information"
      },
      fields: {
        fullName: "Full Name",
        email: "Email",
        organization: "Organization / Hospital Group",
        role: "Role",
        country: "Country",
        department: "Department",
        phone: "Phone",
        facilityType: "Facility Type",
        hospitalSize: "Hospital Size (beds)",
        otherEquipment: "Other equipment",
        targetTimeframe: "Target start timeframe",
        referralSource: "How did you hear about KlinikTrack?",
        comments: "Comments"
      },
      placeholders: {
        fullName: "Your full name",
        email: "your.email@hospital.com",
        organization: "Hospital or organization name",
        role: "Your role or position",
        country: "Select country",
        department: "Select department",
        phone: "+49 123 456 7890",
        facilityType: "Select facility type",
        hospitalSize: "Select size",
        otherEquipment: "Other equipment you'd like to track",
        timeframe: "Select timeframe",
        referral: "Select source",
        comments: "Any specific questions or requirements?"
      },
      optional: "(optional)",
      pleaseSpecify: "(please specify)",
      required: "*",
      gdprConsent: "I agree that my information will be processed to respond to my request and provide demo access.",
      privacyPolicy: "Privacy policy",
      submit: "Request demo access",
      submitting: "Submitting...",
      reviewNote: "Access is reviewed to ensure relevance.",
      errorFillFields: "Please fill in all required fields",
      errorGdpr: "Please accept the privacy policy to continue",
      errorSubmit: "Failed to submit request. Please try again.",
      successToast: "Demo access request submitted successfully"
    },
    
    // Countries
    countries: {
      germany: "Germany",
      austria: "Austria",
      switzerland: "Switzerland",
      luxembourg: "Luxembourg",
      liechtenstein: "Liechtenstein",
      otherEurope: "Other Europe"
    },
    
    // Departments
    departments: {
      operations: "Operations",
      logistics: "Logistics",
      biomedical: "Biomedical",
      it: "IT",
      qualityCompliance: "Quality & Compliance",
      nursing: "Nursing",
      procurement: "Procurement",
      other: "Other"
    },
    
    // Facility Types
    facilityTypes: {
      publicHospital: "Public hospital",
      privateClinic: "Private clinic",
      universityHospital: "University hospital",
      rehabLongterm: "Rehab & long-term care",
      other: "Other"
    },
    
    // Hospital Sizes
    hospitalSizes: {
      under200: "< 200 beds",
      from200to500: "200 – 500 beds",
      from500to1000: "500 – 1000 beds",
      over1000: "1000+ beds"
    },
    
    // Equipment
    equipment: {
      infusionPumps: "Infusion pumps",
      beds: "Beds",
      wheelchairs: "Wheelchairs",
      biomedicalMobile: "Biomedical mobile equipment",
      carts: "Carts",
      cablesAccessories: "Cables & accessories",
      medicationBoxes: "Medication boxes"
    },
    
    // Timeframes
    timeframes: {
      days030: "0 – 30 days",
      months13: "1 – 3 months",
      months36: "3 – 6 months",
      over6months: "> 6 months",
      exploring: "Just exploring"
    },
    
    // Referral Sources
    referralSources: {
      referral: "Referral",
      linkedin: "LinkedIn",
      event: "Event",
      google: "Google",
      partner: "Partner",
      other: "Other"
    },
    
    // Audience Section
    audience: {
      title: "Designed for hospital operations.",
      hospitalManagement: "Hospital management",
      logistics: "Logistics & operations teams",
      biomedical: "Biomedical departments",
      quality: "Quality & compliance"
    },
    
    // FAQ Section
    faq: {
      title: "KlinikTrack — Frequently Asked Questions",
      description: "This FAQ addresses the most common questions from hospital management, decision-makers, and operational teams regarding GDPR, resilience, installation, and daily usage of KlinikTrack.",
      closing: "Clear scope. Clear expectations.",
      ctaText: "If you have additional regulatory, technical, or operational questions, you can request access to the demo platform or contact our team.",
      ctaButton: "Request demo access",
      categories: {
        gdpr: "GDPR & Data Protection",
        resilience: "Resilience & Reliability",
        installation: "Installation & On-Site Impact",
        staff: "Staff & Patient Acceptance",
        buyers: "Buyers & Decision-Makers",
        scope: "Scope & Expectations"
      },
      questions: {
        gdpr1q: "Does KlinikTrack process patient or medical data?",
        gdpr1a: "No. KlinikTrack tracks equipment and objects only. It does not process patient data, medical records, or staff personal data.",
        gdpr2q: "Is KlinikTrack GDPR compliant?",
        gdpr2a: "Yes. KlinikTrack is designed according to GDPR principles by design and by default, including data minimization, purpose limitation, access control, and EU / Switzerland-based infrastructure.",
        gdpr3q: "Where is the data hosted?",
        gdpr3a: "Data is hosted on EU and Switzerland-based infrastructure, aligned with GDPR and Swiss FADP requirements.",
        gdpr4q: "Who owns the data?",
        gdpr4a: "The hospital remains the data owner. KlinikTrack acts as a data processor for operational tracking data related to equipment.",
        gdpr5q: "How long is data retained?",
        gdpr5a: "Data is retained only as long as necessary for operational purposes and contractual obligations. Retention periods are clearly defined and can be adapted per project.",
        res1q: "What happens if the network is temporarily unavailable?",
        res1a: "KlinikTrack is designed to be resilient. Temporary connectivity loss does not stop operations. Data is transmitted once connectivity is restored.",
        res2q: "Is KlinikTrack a critical hospital system?",
        res2a: "No. KlinikTrack is an operational support system, not a life-critical or clinical system. Hospital operations continue normally even if the system is unavailable.",
        res3q: "Does KlinikTrack require integration with existing IT systems?",
        res3a: "No. KlinikTrack does not require deep integration with HIS, EHR, or BMS systems. It operates independently and can be removed without impact.",
        inst1q: "Will installation disturb patients or staff?",
        inst1a: "No. Installation is non-intrusive, silent, and performed without interrupting care activities.",
        inst2q: "Are rooms or equipment taken out of service during installation?",
        inst2a: "No. Tags are attached quickly to mobile assets without requiring downtime or room shutdown.",
        inst3q: "How long does installation take?",
        inst3a: "Typical pilot installations take a few hours, depending on the number of assets. Deployment can be staged by department.",
        staff1q: "Will staff feel monitored?",
        staff1a: "No. KlinikTrack tracks equipment, not people. There is no staff tracking, no performance monitoring, and no individual identification.",
        staff2q: "Will patients notice the system?",
        staff2a: "In most cases, no. Devices are discreet and installed on equipment, not on patients or rooms.",
        staff3q: "Do staff need training?",
        staff3a: "Minimal training is required. The dashboard is intuitive, and most users can get started in less than 15 minutes.",
        buy1q: "Who typically uses KlinikTrack?",
        buy1a: "Logistics and operations teams, biomedical departments, facility management, and quality & compliance teams.",
        buy2q: "Who usually decides to buy KlinikTrack?",
        buy2a: "Hospital management and operations leadership, with input from procurement, IT, and biomedical teams.",
        buy3q: "What problem does KlinikTrack solve first?",
        buy3a: "It reduces time lost searching for equipment and provides clear visibility into asset availability.",
        buy4q: "Is KlinikTrack a medical device?",
        buy4a: "No. KlinikTrack is not classified as a medical device and does not support clinical decision-making.",
        scope1q: "What does KlinikTrack do?",
        scope1a: "It provides operational visibility of mobile assets, supports logistics and planning, and helps reduce search time and over-purchasing.",
        scope2q: "What does KlinikTrack not do?",
        scope2a: "It does not track patients or staff, does not replace clinical systems, and does not automate medical decisions."
      }
    },
    
    // Footer
    footer: {
      solutions: "Solutions",
      assetTracking: "Asset Tracking (KlinikTrack)",
      elderaCare: "Eldera Care",
      healthMonitoring: "Health Monitoring",
      mobileCare: "Mobile Care",
      allSolutions: "All solutions",
      company: "Company",
      legal: "Legal",
      privacyPolicy: "Privacy Policy",
      gdpr: "GDPR",
      terms: "Terms",
      imprint: "Imprint / Legal notice",
      copyright: "© 2026 KlinikTrack. All rights reserved. Operated by 0g Holding SA."
    },
    
    // Privacy Policy
    privacy: {
      title: "Privacy Policy (GDPR – DACH)",
      lastUpdated: "Last updated"
    }
  },
  
  de: {
    // Navigation
    nav: {
      requestDemo: "Demo anfordern",
      backToHome: "Zurück zur Startseite"
    },
    
    // Hero Section
    hero: {
      title: "KlinikTrack",
      subtitle: "Krankenhaus-Asset-Tracking — Einfach gemacht",
      tagline1: "Plug & Play-Sichtbarkeit für kritische medizinische Geräte.",
      tagline2: "Entwickelt für den Krankenhausbetrieb.",
      cta: "Zugang zur Demo-Plattform anfordern"
    },
    
    // Problem Section
    problem: {
      title: "Krankenhäuser verlieren Zeit — nicht Geräte.",
      description: "Täglich verschwenden Krankenhauspersonal wertvolle Zeit mit der Suche nach mobilen Geräten. Assets bewegen sich zwischen Abteilungen ohne Sichtbarkeit, was zu Ineffizienz und operativem Druck führt.",
      bullet1: "Geräte sind schwer über Etagen und Stationen zu lokalisieren",
      bullet2: "Die Auslastung wird schlecht verstanden",
      bullet3: "\"Vorsichtshalber\"-Käufe werden zur Norm",
      bullet4: "Der operative Stress steigt"
    },
    
    // Solution Section
    solution: {
      title: "Operative Sichtbarkeit, ohne Störung.",
      description: "KlinikTrack bietet Krankenhäusern eine einfache Möglichkeit, kritische Assets nach Zone, Etage oder Abteilung zu lokalisieren und zu überwachen — ohne klinische Arbeitsabläufe oder Patientendaten zu berühren.",
      feature1Title: "Asset-Sichtbarkeit nach Standort",
      feature1Desc: "Wissen Sie jederzeit, wo sich Ihre Geräte befinden.",
      feature2Title: "Einfache Warnungen & Statusüberwachung",
      feature2Desc: "Werden Sie benachrichtigt, wenn sich der Gerätestatus ändert.",
      feature3Title: "Nutzungs- und Verfügbarkeitseinblicke",
      feature3Desc: "Verstehen Sie, wie Ihre Assets genutzt werden.",
      closing: "Gebaut für den Betrieb. Entwickelt für Krankenhäuser."
    },
    
    // Track Section
    track: {
      title: "Was können Sie mit KlinikTrack tracken?",
      description: "KlinikTrack konzentriert sich auf mobile, hochwertige und häufig verlegte Krankenhaus-Assets, die für den täglichen Betrieb kritisch sind.",
      items: {
        infusionPumps: "Infusionspumpen",
        beds: "Krankenhausbetten",
        wheelchairs: "Rollstühle",
        biomedical: "Mobile biomedizinische Geräte",
        carts: "Pflege- & Servicewagen",
        cables: "Kabel & Zubehör",
        medication: "Medikamentenboxen & Lagerbehälter"
      },
      moreText: "Und mehr... Jedes mobile Asset, das sich zwischen Räumen, Etagen oder Abteilungen bewegt, kann getrackt werden.",
      disclaimer: "KlinikTrack trackt Geräte und Objekte — keine Patienten oder Personal. Es werden keine persönlichen oder medizinischen Daten erfasst."
    },
    
    // Why Section
    why: {
      title: "Warum KlinikTrack?",
      plugPlay: {
        title: "Plug & Play",
        desc: "Schnelle Bereitstellung, keine aufwändige IT-Integration, reversible Installation."
      },
      aiAssisted: {
        title: "KI-unterstützt",
        desc: "Intelligente Warnungen und grundlegende Nutzungseinblicke zur Unterstützung operativer Entscheidungen — keine Automatisierung."
      },
      resilient: {
        title: "Resilient by Design",
        desc: "Batteriebetriebene Geräte, tolerant gegenüber partieller Konnektivität, entwickelt für Krankenhausumgebungen."
      },
      gdpr: {
        title: "DSGVO & Datenschutz zuerst",
        desc: "Keine Patientendaten, kein Personal-Tracking, EU-gehostete Infrastruktur."
      },
      operational: {
        title: "Operativ, nicht klinisch",
        desc: "Entscheidungsunterstützung für den Betrieb. Keine medizinischen Ansprüche. Keine klinische Verantwortung."
      }
    },
    
    // Preview Section
    preview: {
      title: "Ein klares operatives Dashboard.",
      description: "KlinikTrack bietet ein einfaches, intuitives Dashboard, das für den Krankenhausbetrieb entwickelt wurde.",
      feature1: "Asset-Liste & Status",
      feature2: "Standort und zuletzt gesehen",
      feature3: "Warnungen und Verlauf"
    },
    
    // Demo Form
    form: {
      title: "Zugang zur KlinikTrack Demo-Plattform",
      description: "Entdecken Sie, wie Krankenhäuser in wenigen Minuten operative Sichtbarkeit erlangen können.",
      successTitle: "Anfrage erhalten",
      successMessage: "Vielen Dank für Ihr Interesse an KlinikTrack. Wir werden Ihre Anfrage prüfen und uns in Kürze bei Ihnen melden.",
      sections: {
        yourInfo: "Ihre Informationen",
        hospitalInfo: "Krankenhaus-Informationen",
        whatToTrack: "Was möchten Sie tracken?",
        timeline: "Projektzeitplan",
        additional: "Zusätzliche Informationen"
      },
      fields: {
        fullName: "Vollständiger Name",
        email: "E-Mail",
        organization: "Organisation / Krankenhausgruppe",
        role: "Rolle",
        country: "Land",
        department: "Abteilung",
        phone: "Telefon",
        facilityType: "Einrichtungstyp",
        hospitalSize: "Krankenhausgröße (Betten)",
        otherEquipment: "Andere Geräte",
        targetTimeframe: "Ziel-Starttermin",
        referralSource: "Wie haben Sie von KlinikTrack erfahren?",
        comments: "Kommentare"
      },
      placeholders: {
        fullName: "Ihr vollständiger Name",
        email: "ihre.email@krankenhaus.de",
        organization: "Krankenhaus- oder Organisationsname",
        role: "Ihre Rolle oder Position",
        country: "Land auswählen",
        department: "Abteilung auswählen",
        phone: "+49 123 456 7890",
        facilityType: "Einrichtungstyp auswählen",
        hospitalSize: "Größe auswählen",
        otherEquipment: "Andere Geräte, die Sie tracken möchten",
        timeframe: "Zeitrahmen auswählen",
        referral: "Quelle auswählen",
        comments: "Spezifische Fragen oder Anforderungen?"
      },
      optional: "(optional)",
      pleaseSpecify: "(bitte angeben)",
      required: "*",
      gdprConsent: "Ich stimme zu, dass meine Daten zur Bearbeitung meiner Anfrage und zur Bereitstellung des Demo-Zugangs verarbeitet werden.",
      privacyPolicy: "Datenschutzerklärung",
      submit: "Demo-Zugang anfordern",
      submitting: "Wird gesendet...",
      reviewNote: "Der Zugang wird auf Relevanz geprüft.",
      errorFillFields: "Bitte füllen Sie alle erforderlichen Felder aus",
      errorGdpr: "Bitte akzeptieren Sie die Datenschutzerklärung, um fortzufahren",
      errorSubmit: "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
      successToast: "Demo-Zugangsanfrage erfolgreich gesendet"
    },
    
    // Countries
    countries: {
      germany: "Deutschland",
      austria: "Österreich",
      switzerland: "Schweiz",
      luxembourg: "Luxemburg",
      liechtenstein: "Liechtenstein",
      otherEurope: "Anderes Europa"
    },
    
    // Departments
    departments: {
      operations: "Betrieb",
      logistics: "Logistik",
      biomedical: "Biomedizin",
      it: "IT",
      qualityCompliance: "Qualität & Compliance",
      nursing: "Pflege",
      procurement: "Beschaffung",
      other: "Sonstiges"
    },
    
    // Facility Types
    facilityTypes: {
      publicHospital: "Öffentliches Krankenhaus",
      privateClinic: "Privatklinik",
      universityHospital: "Universitätsklinikum",
      rehabLongterm: "Reha & Langzeitpflege",
      other: "Sonstiges"
    },
    
    // Hospital Sizes
    hospitalSizes: {
      under200: "< 200 Betten",
      from200to500: "200 – 500 Betten",
      from500to1000: "500 – 1000 Betten",
      over1000: "1000+ Betten"
    },
    
    // Equipment
    equipment: {
      infusionPumps: "Infusionspumpen",
      beds: "Betten",
      wheelchairs: "Rollstühle",
      biomedicalMobile: "Mobile biomedizinische Geräte",
      carts: "Wagen",
      cablesAccessories: "Kabel & Zubehör",
      medicationBoxes: "Medikamentenboxen"
    },
    
    // Timeframes
    timeframes: {
      days030: "0 – 30 Tage",
      months13: "1 – 3 Monate",
      months36: "3 – 6 Monate",
      over6months: "> 6 Monate",
      exploring: "Nur erkunden"
    },
    
    // Referral Sources
    referralSources: {
      referral: "Empfehlung",
      linkedin: "LinkedIn",
      event: "Veranstaltung",
      google: "Google",
      partner: "Partner",
      other: "Sonstiges"
    },
    
    // Audience Section
    audience: {
      title: "Entwickelt für den Krankenhausbetrieb.",
      hospitalManagement: "Krankenhausmanagement",
      logistics: "Logistik- & Betriebsteams",
      biomedical: "Biomedizinische Abteilungen",
      quality: "Qualität & Compliance"
    },
    
    // FAQ Section
    faq: {
      title: "KlinikTrack — Häufig gestellte Fragen",
      description: "Diese FAQ beantwortet die häufigsten Fragen von Krankenhausmanagement, Entscheidungsträgern und operativen Teams zu DSGVO, Resilienz, Installation und täglicher Nutzung von KlinikTrack.",
      closing: "Klarer Umfang. Klare Erwartungen.",
      ctaText: "Bei zusätzlichen regulatorischen, technischen oder operativen Fragen können Sie Zugang zur Demo-Plattform anfordern oder unser Team kontaktieren.",
      ctaButton: "Demo-Zugang anfordern",
      categories: {
        gdpr: "DSGVO & Datenschutz",
        resilience: "Resilienz & Zuverlässigkeit",
        installation: "Installation & Vor-Ort-Auswirkungen",
        staff: "Personal- & Patientenakzeptanz",
        buyers: "Käufer & Entscheidungsträger",
        scope: "Umfang & Erwartungen"
      },
      questions: {
        gdpr1q: "Verarbeitet KlinikTrack Patienten- oder medizinische Daten?",
        gdpr1a: "Nein. KlinikTrack trackt nur Geräte und Objekte. Es werden keine Patientendaten, Krankenakten oder persönliche Personaldaten verarbeitet.",
        gdpr2q: "Ist KlinikTrack DSGVO-konform?",
        gdpr2a: "Ja. KlinikTrack ist nach DSGVO-Prinzipien by Design und by Default konzipiert, einschließlich Datenminimierung, Zweckbindung, Zugriffskontrolle und EU/Schweiz-basierter Infrastruktur.",
        gdpr3q: "Wo werden die Daten gehostet?",
        gdpr3a: "Daten werden auf EU- und Schweiz-basierter Infrastruktur gehostet, in Übereinstimmung mit DSGVO- und Schweizer DSG-Anforderungen.",
        gdpr4q: "Wem gehören die Daten?",
        gdpr4a: "Das Krankenhaus bleibt Dateneigentümer. KlinikTrack fungiert als Datenverarbeiter für operative Tracking-Daten zu Geräten.",
        gdpr5q: "Wie lange werden Daten aufbewahrt?",
        gdpr5a: "Daten werden nur so lange aufbewahrt, wie für operative Zwecke und vertragliche Verpflichtungen erforderlich. Aufbewahrungsfristen sind klar definiert und können pro Projekt angepasst werden.",
        res1q: "Was passiert, wenn das Netzwerk vorübergehend nicht verfügbar ist?",
        res1a: "KlinikTrack ist resilient konzipiert. Vorübergehender Konnektivitätsverlust stoppt den Betrieb nicht. Daten werden übertragen, sobald die Konnektivität wiederhergestellt ist.",
        res2q: "Ist KlinikTrack ein kritisches Krankenhaussystem?",
        res2a: "Nein. KlinikTrack ist ein operatives Unterstützungssystem, kein lebenskritisches oder klinisches System. Der Krankenhausbetrieb läuft normal weiter, auch wenn das System nicht verfügbar ist.",
        res3q: "Erfordert KlinikTrack eine Integration mit bestehenden IT-Systemen?",
        res3a: "Nein. KlinikTrack erfordert keine tiefe Integration mit KIS, EPA oder GLT-Systemen. Es arbeitet unabhängig und kann ohne Auswirkungen entfernt werden.",
        inst1q: "Wird die Installation Patienten oder Personal stören?",
        inst1a: "Nein. Die Installation ist unaufdringlich, leise und wird ohne Unterbrechung der Pflegeaktivitäten durchgeführt.",
        inst2q: "Werden Räume oder Geräte während der Installation außer Betrieb genommen?",
        inst2a: "Nein. Tags werden schnell an mobilen Assets angebracht, ohne dass Ausfallzeiten oder Raumabschaltungen erforderlich sind.",
        inst3q: "Wie lange dauert die Installation?",
        inst3a: "Typische Pilotinstallationen dauern einige Stunden, je nach Anzahl der Assets. Die Bereitstellung kann abteilungsweise erfolgen.",
        staff1q: "Wird sich das Personal überwacht fühlen?",
        staff1a: "Nein. KlinikTrack trackt Geräte, keine Menschen. Es gibt kein Personal-Tracking, keine Leistungsüberwachung und keine individuelle Identifizierung.",
        staff2q: "Werden Patienten das System bemerken?",
        staff2a: "In den meisten Fällen nein. Geräte sind diskret und werden an Geräten installiert, nicht an Patienten oder in Räumen.",
        staff3q: "Benötigt das Personal eine Schulung?",
        staff3a: "Minimale Schulung ist erforderlich. Das Dashboard ist intuitiv, und die meisten Benutzer können in weniger als 15 Minuten starten.",
        buy1q: "Wer nutzt KlinikTrack typischerweise?",
        buy1a: "Logistik- und Betriebsteams, biomedizinische Abteilungen, Facility Management und Qualitäts- & Compliance-Teams.",
        buy2q: "Wer entscheidet normalerweise über den Kauf von KlinikTrack?",
        buy2a: "Krankenhausmanagement und Betriebsleitung, mit Input von Beschaffung, IT und biomedizinischen Teams.",
        buy3q: "Welches Problem löst KlinikTrack zuerst?",
        buy3a: "Es reduziert die Zeit, die mit der Suche nach Geräten verloren geht, und bietet klare Sichtbarkeit der Asset-Verfügbarkeit.",
        buy4q: "Ist KlinikTrack ein Medizinprodukt?",
        buy4a: "Nein. KlinikTrack ist nicht als Medizinprodukt klassifiziert und unterstützt keine klinischen Entscheidungen.",
        scope1q: "Was macht KlinikTrack?",
        scope1a: "Es bietet operative Sichtbarkeit mobiler Assets, unterstützt Logistik und Planung und hilft, Suchzeit und Überkäufe zu reduzieren.",
        scope2q: "Was macht KlinikTrack nicht?",
        scope2a: "Es trackt keine Patienten oder Personal, ersetzt keine klinischen Systeme und automatisiert keine medizinischen Entscheidungen."
      }
    },
    
    // Footer
    footer: {
      solutions: "Lösungen",
      assetTracking: "Asset Tracking (KlinikTrack)",
      elderaCare: "Eldera Care",
      healthMonitoring: "Gesundheitsmonitoring",
      mobileCare: "Mobile Care",
      allSolutions: "Alle Lösungen",
      company: "Unternehmen",
      legal: "Rechtliches",
      privacyPolicy: "Datenschutzerklärung",
      gdpr: "DSGVO",
      terms: "AGB",
      imprint: "Impressum",
      copyright: "© 2026 KlinikTrack. Alle Rechte vorbehalten. Betrieben von 0g Holding SA."
    },
    
    // Privacy Policy
    privacy: {
      title: "Datenschutzerklärung (DSGVO – DACH)",
      lastUpdated: "Zuletzt aktualisiert"
    }
  },
  
  // French translations
  fr: {
    nav: {
      requestDemo: "Demander une démo",
      backToHome: "Retour à l'accueil"
    },
    hero: {
      title: "KlinikTrack",
      subtitle: "Suivi des équipements hospitaliers — Simplifié",
      tagline1: "Visibilité plug & play pour les équipements médicaux critiques.",
      tagline2: "Conçu pour les opérations hospitalières.",
      cta: "Demander l'accès à la plateforme de démonstration"
    },
    problem: {
      title: "Les hôpitaux perdent du temps — pas des équipements.",
      description: "Chaque jour, le personnel hospitalier perd un temps précieux à chercher des équipements mobiles. Les actifs circulent entre les services sans visibilité, entraînant inefficacité et pression opérationnelle.",
      bullet1: "Les équipements sont difficiles à localiser entre les étages et services",
      bullet2: "L'utilisation est mal comprise",
      bullet3: "Les achats \"au cas où\" deviennent la norme",
      bullet4: "Le stress opérationnel augmente"
    },
    solution: {
      title: "Visibilité opérationnelle, sans perturbation.",
      description: "KlinikTrack offre aux hôpitaux un moyen simple de localiser et surveiller les équipements critiques par zone, étage ou département — sans toucher aux workflows cliniques ni aux données patients.",
      feature1Title: "Visibilité des actifs par localisation",
      feature1Desc: "Sachez où se trouve votre équipement à tout moment.",
      feature2Title: "Alertes simples et suivi de statut",
      feature2Desc: "Soyez notifié quand le statut d'un équipement change.",
      feature3Title: "Analyses d'utilisation et de disponibilité",
      feature3Desc: "Comprenez comment vos actifs sont utilisés.",
      closing: "Conçu pour les opérations. Pensé pour les hôpitaux."
    },
    track: {
      title: "Que pouvez-vous suivre avec KlinikTrack ?",
      description: "KlinikTrack se concentre sur les équipements hospitaliers mobiles, de grande valeur et fréquemment égarés, essentiels aux opérations quotidiennes.",
      items: {
        infusionPumps: "Pompes à perfusion",
        beds: "Lits d'hôpital",
        wheelchairs: "Fauteuils roulants",
        biomedical: "Équipements biomédicaux mobiles",
        carts: "Chariots de soins et de service",
        cables: "Câbles et accessoires",
        medicationBoxes: "Boîtes de médicaments"
      },
      disclaimer: "KlinikTrack ne suit pas les patients, le personnel ou les actifs fixes."
    },
    why: {
      title: "Pourquoi KlinikTrack ?",
      features: {
        simpleTitle: "Simple et non intrusif",
        simpleDesc: "Aucune intégration avec les systèmes cliniques ni perturbation des workflows.",
        realTimeTitle: "Données en temps réel",
        realTimeDesc: "Visibilité instantanée de la localisation et du statut des équipements.",
        privacyTitle: "Respectueux de la vie privée",
        privacyDesc: "KlinikTrack ne collecte ni ne traite de données patients.",
        scaleTitle: "Construit pour évoluer",
        scaleDesc: "Des petites unités aux grands réseaux hospitaliers multi-sites.",
        roiTitle: "ROI clair",
        roiDesc: "Réduisez le temps de recherche, améliorez l'utilisation et évitez les achats superflus."
      }
    },
    preview: {
      title: "Aperçu de la plateforme",
      description: "Une interface simple et intuitive pour localiser et gérer les équipements en temps réel."
    },
    audience: {
      title: "Conçu pour les opérations hospitalières.",
      groups: {
        management: "Direction hospitalière",
        logistics: "Équipes logistique et opérations",
        biomedical: "Départements biomédicaux",
        quality: "Qualité et conformité"
      }
    },
    form: {
      title: "Accéder à la plateforme de démonstration KlinikTrack",
      subtitle: "Remplissez vos informations ci-dessous pour demander l'accès.",
      sections: {
        yourInfo: "Vos informations",
        hospitalInfo: "Informations sur l'hôpital",
        whatToTrack: "Que souhaitez-vous suivre ?",
        timeline: "Calendrier du projet",
        additional: "Informations complémentaires"
      },
      fields: {
        fullName: "Nom complet",
        email: "E-mail",
        organization: "Organisation / Groupe hospitalier",
        role: "Fonction",
        country: "Pays",
        department: "Département",
        phone: "Téléphone",
        facilityType: "Type d'établissement",
        hospitalSize: "Taille de l'hôpital (lits)",
        otherEquipment: "Autres équipements",
        targetTimeframe: "Délai cible de démarrage",
        referralSource: "Comment avez-vous entendu parler de KlinikTrack ?",
        comments: "Commentaires"
      },
      placeholders: {
        fullName: "Votre nom complet",
        email: "votre@email.com",
        organization: "Nom de votre hôpital ou groupe",
        role: "Ex: Responsable logistique",
        department: "Sélectionner un département",
        phone: "Numéro de téléphone (optionnel)",
        facilityType: "Sélectionner le type d'établissement",
        hospitalSize: "Sélectionner la taille",
        otherEquipment: "Autres équipements que vous souhaitez suivre",
        timeframe: "Sélectionner un délai",
        referral: "Sélectionner une source",
        comments: "Questions spécifiques ou exigences ?"
      },
      gdprConsent: "J'accepte que mes informations soient traitées pour répondre à ma demande et fournir l'accès à la démo.",
      privacyPolicy: "Politique de confidentialité",
      submit: "Demander l'accès à la démo",
      submitting: "Envoi en cours...",
      reviewNote: "L'accès est examiné pour assurer la pertinence.",
      pleaseSpecify: "(veuillez préciser)",
      optional: "(optionnel)",
      success: {
        title: "Demande envoyée !",
        message: "Merci pour votre intérêt pour KlinikTrack. Notre équipe examinera votre demande et vous contactera bientôt.",
        goBack: "Retour à l'accueil"
      }
    },
    countries: {
      germany: "Allemagne",
      austria: "Autriche",
      switzerland: "Suisse",
      luxembourg: "Luxembourg",
      liechtenstein: "Liechtenstein",
      otherEurope: "Autre Europe"
    },
    departments: {
      operations: "Opérations",
      logistics: "Logistique",
      biomedical: "Biomédical",
      it: "IT",
      qualityCompliance: "Qualité & Conformité",
      nursing: "Soins infirmiers",
      procurement: "Achats",
      other: "Autre"
    },
    facilityTypes: {
      publicHospital: "Hôpital public",
      privateClinic: "Clinique privée",
      universityHospital: "CHU",
      rehabLongterm: "Réhabilitation & soins longue durée",
      other: "Autre"
    },
    hospitalSizes: {
      under200: "< 200 lits",
      size200to500: "200 – 500 lits",
      size500to1000: "500 – 1 000 lits",
      over1000: "1 000+ lits"
    },
    equipment: {
      infusion_pumps: "Pompes à perfusion",
      beds: "Lits",
      wheelchairs: "Fauteuils roulants",
      biomedical_mobile: "Équipements biomédicaux mobiles",
      carts: "Chariots",
      cables_accessories: "Câbles & accessoires",
      medication_boxes: "Boîtes de médicaments"
    },
    timeframes: {
      days0to30: "0 – 30 jours",
      months1to3: "1 – 3 mois",
      months3to6: "3 – 6 mois",
      over6months: "> 6 mois",
      exploring: "En exploration"
    },
    referralSources: {
      referral: "Recommandation",
      linkedin: "LinkedIn",
      event: "Événement",
      google: "Google",
      partner: "Partenaire",
      other: "Autre"
    },
    faq: {
      title: "Questions fréquentes",
      categories: {
        dataPrivacy: "Données & Confidentialité",
        technical: "Technique & Installation",
        staffPatients: "Personnel & Patients",
        buyingProcess: "Processus d'achat",
        scope: "Périmètre du produit"
      },
      data1q: "Quelles données KlinikTrack collecte-t-il ?",
      data1a: "Uniquement les données de localisation des équipements (par zone/étage), le statut et les métadonnées d'utilisation. Aucune donnée patient n'est collectée.",
      data2q: "Où les données sont-elles stockées ?",
      data2a: "Les données sont stockées dans des centres de données européens conformes au RGPD (Allemagne et Suisse).",
      data3q: "Les données sont-elles chiffrées ?",
      data3a: "Oui. Les données en transit et au repos sont chiffrées selon les standards actuels.",
      data4q: "Qui peut accéder aux données ?",
      data4a: "Seuls les utilisateurs hospitaliers autorisés via un contrôle d'accès basé sur les rôles. KlinikTrack n'accède aux données qu'avec autorisation explicite à des fins de support.",
      tech1q: "Quelle technologie est utilisée ?",
      tech1a: "KlinikTrack utilise une combinaison de technologies sans fil (BLE et WiFi) selon l'environnement hospitalier.",
      tech2q: "KlinikTrack nécessite-t-il une intégration IT ?",
      tech2a: "Non. KlinikTrack fonctionne sur une infrastructure indépendante et ne nécessite aucune intégration avec les systèmes cliniques.",
      inst1q: "Comment les tags sont-ils attachés ?",
      inst1a: "Les tags sont de petits appareils montés ou attachés aux équipements. L'installation est rapide et ne nécessite pas de modifications de l'équipement.",
      inst2q: "La salle d'opération doit-elle être fermée pour l'installation ?",
      inst2a: "Non. Les tags sont rapidement attachés aux équipements mobiles, sans nécessiter de temps d'arrêt ni de fermeture de salle.",
      inst3q: "Combien de temps dure l'installation ?",
      inst3a: "Les installations pilotes typiques prennent quelques heures selon le nombre d'équipements. Le déploiement peut se faire par département.",
      staff1q: "Le personnel se sentira-t-il surveillé ?",
      staff1a: "Non. KlinikTrack suit les équipements, pas les personnes. Il n'y a pas de suivi du personnel, de surveillance des performances ou d'identification individuelle.",
      staff2q: "Les patients remarqueront-ils le système ?",
      staff2a: "Dans la plupart des cas, non. Les appareils sont discrets et installés sur les équipements, pas sur les patients ou dans les chambres.",
      staff3q: "Le personnel a-t-il besoin d'une formation ?",
      staff3a: "Une formation minimale est nécessaire. Le tableau de bord est intuitif et la plupart des utilisateurs peuvent commencer en moins de 15 minutes.",
      buy1q: "Qui utilise généralement KlinikTrack ?",
      buy1a: "Les équipes logistique et opérations, les départements biomédicaux, la gestion des installations et les équipes qualité & conformité.",
      buy2q: "Qui décide généralement de l'achat de KlinikTrack ?",
      buy2a: "La direction hospitalière et opérationnelle, avec l'avis des équipes achats, IT et biomédicales.",
      buy3q: "Quel problème KlinikTrack résout-il en premier ?",
      buy3a: "Il réduit le temps perdu à chercher des équipements et fournit une visibilité claire de la disponibilité des actifs.",
      buy4q: "KlinikTrack est-il un dispositif médical ?",
      buy4a: "Non. KlinikTrack n'est pas classé comme dispositif médical et ne prend pas en charge les décisions cliniques.",
      scope1q: "Que fait KlinikTrack ?",
      scope1a: "Il fournit une visibilité opérationnelle des équipements mobiles, soutient la logistique et la planification, et aide à réduire le temps de recherche et les achats superflus.",
      scope2q: "Que ne fait pas KlinikTrack ?",
      scope2a: "Il ne suit pas les patients ou le personnel, ne remplace pas les systèmes cliniques et n'automatise pas les décisions médicales."
    },
    footer: {
      solutions: "Solutions",
      assetTracking: "Suivi d'équipements (KlinikTrack)",
      elderaCare: "Eldera Care",
      healthMonitoring: "Surveillance de santé",
      mobileCare: "Soins mobiles",
      allSolutions: "Toutes les solutions",
      company: "Entreprise",
      legal: "Légal",
      privacyPolicy: "Politique de confidentialité",
      gdpr: "RGPD",
      terms: "CGU",
      imprint: "Mentions légales",
      copyright: "© 2026 KlinikTrack. Tous droits réservés. Opéré par 0g Holding SA."
    },
    privacy: {
      title: "Politique de confidentialité (RGPD)",
      lastUpdated: "Dernière mise à jour"
    }
  },
  
  // Latvian translations
  lv: {
    nav: {
      requestDemo: "Pieprasīt demo",
      backToHome: "Atpakaļ uz sākumu"
    },
    hero: {
      title: "KlinikTrack",
      subtitle: "Slimnīcas aprīkojuma izsekošana — Vienkāršota",
      tagline1: "Plug & play redzamība kritiskajam medicīniskajam aprīkojumam.",
      tagline2: "Izstrādāts slimnīcas operācijām.",
      cta: "Pieprasīt piekļuvi demo platformai"
    },
    problem: {
      title: "Slimnīcas zaudē laiku — nevis aprīkojumu.",
      description: "Katru dienu slimnīcas personāls tērē vērtīgu laiku, meklējot mobilo aprīkojumu. Aktīvi pārvietojas starp nodaļām bez redzamības, izraisot neefektivitāti un operatīvo spiedienu.",
      bullet1: "Aprīkojumu ir grūti atrast starp stāviem un nodaļām",
      bullet2: "Izmantošana ir slikti izprasta",
      bullet3: "\"Katram gadījumam\" pirkumi kļūst par normu",
      bullet4: "Operatīvais stress pieaug"
    },
    solution: {
      title: "Operatīvā redzamība bez traucējumiem.",
      description: "KlinikTrack sniedz slimnīcām vienkāršu veidu, kā atrast un uzraudzīt kritisko aprīkojumu pēc zonas, stāva vai nodaļas — neaizskarot klīniskos procesus vai pacientu datus.",
      feature1Title: "Aktīvu redzamība pēc atrašanās vietas",
      feature1Desc: "Ziniet, kur atrodas jūsu aprīkojums jebkurā laikā.",
      feature2Title: "Vienkārši brīdinājumi un statusa uzraudzība",
      feature2Desc: "Saņemiet paziņojumus, kad mainās aprīkojuma statuss.",
      feature3Title: "Izmantošanas un pieejamības ieskati",
      feature3Desc: "Izprotiet, kā tiek izmantoti jūsu aktīvi.",
      closing: "Veidots operācijām. Izstrādāts slimnīcām."
    },
    track: {
      title: "Ko varat izsekot ar KlinikTrack?",
      description: "KlinikTrack koncentrējas uz mobilajiem, augstvērtīgajiem un bieži pazaudētajiem slimnīcas aktīviem, kas ir kritiski ikdienas darbībām.",
      items: {
        infusionPumps: "Infūzijas sūkņi",
        beds: "Slimnīcas gultas",
        wheelchairs: "Ratiņkrēsli",
        biomedical: "Mobilais biomedicīnas aprīkojums",
        carts: "Aprūpes un servisa rati",
        cables: "Kabeļi un piederumi",
        medicationBoxes: "Medikamentu kastes"
      },
      disclaimer: "KlinikTrack neizseko pacientus, personālu vai fiksētos aktīvus."
    },
    why: {
      title: "Kāpēc KlinikTrack?",
      features: {
        simpleTitle: "Vienkāršs un neuzbāzīgs",
        simpleDesc: "Nav integrācijas ar klīniskajām sistēmām vai procesu traucējumiem.",
        realTimeTitle: "Reāllaika dati",
        realTimeDesc: "Tūlītēja redzamība par aprīkojuma atrašanās vietu un statusu.",
        privacyTitle: "Privātumam draudzīgs",
        privacyDesc: "KlinikTrack nevāc un neapstrādā pacientu datus.",
        scaleTitle: "Veidots mērogošanai",
        scaleDesc: "No mazām vienībām līdz lieliem daudzvietu slimnīcu tīkliem.",
        roiTitle: "Skaidrs ROI",
        roiDesc: "Samaziniet meklēšanas laiku, uzlabojiet izmantošanu un izvairieties no nevajadzīgiem pirkumiem."
      }
    },
    preview: {
      title: "Platformas priekšskatījums",
      description: "Vienkārša, intuitīva saskarne aprīkojuma atrašanai un pārvaldībai reāllaikā."
    },
    audience: {
      title: "Izstrādāts slimnīcas operācijām.",
      groups: {
        management: "Slimnīcas vadība",
        logistics: "Loģistikas un operāciju komandas",
        biomedical: "Biomedicīnas nodaļas",
        quality: "Kvalitāte un atbilstība"
      }
    },
    form: {
      title: "Piekļūt KlinikTrack demo platformai",
      subtitle: "Aizpildiet informāciju zemāk, lai pieprasītu piekļuvi.",
      sections: {
        yourInfo: "Jūsu informācija",
        hospitalInfo: "Slimnīcas informācija",
        whatToTrack: "Ko vēlaties izsekot?",
        timeline: "Projekta laika grafiks",
        additional: "Papildu informācija"
      },
      fields: {
        fullName: "Pilns vārds",
        email: "E-pasts",
        organization: "Organizācija / Slimnīcu grupa",
        role: "Amats",
        country: "Valsts",
        department: "Nodaļa",
        phone: "Tālrunis",
        facilityType: "Iestādes tips",
        hospitalSize: "Slimnīcas izmērs (gultas)",
        otherEquipment: "Cits aprīkojums",
        targetTimeframe: "Mērķa sākuma termiņš",
        referralSource: "Kā uzzinājāt par KlinikTrack?",
        comments: "Komentāri"
      },
      placeholders: {
        fullName: "Jūsu pilns vārds",
        email: "jusu@epasts.lv",
        organization: "Slimnīcas vai grupas nosaukums",
        role: "Piem.: Loģistikas vadītājs",
        department: "Izvēlieties nodaļu",
        phone: "Tālruņa numurs (neobligāts)",
        facilityType: "Izvēlieties iestādes tipu",
        hospitalSize: "Izvēlieties izmēru",
        otherEquipment: "Cits aprīkojums, ko vēlaties izsekot",
        timeframe: "Izvēlieties termiņu",
        referral: "Izvēlieties avotu",
        comments: "Specifiski jautājumi vai prasības?"
      },
      gdprConsent: "Piekrītu, ka mana informācija tiks apstrādāta, lai atbildētu uz manu pieprasījumu un nodrošinātu demo piekļuvi.",
      privacyPolicy: "Privātuma politika",
      submit: "Pieprasīt demo piekļuvi",
      submitting: "Nosūta...",
      reviewNote: "Piekļuve tiek pārskatīta, lai nodrošinātu atbilstību.",
      pleaseSpecify: "(lūdzu, precizējiet)",
      optional: "(neobligāts)",
      success: {
        title: "Pieprasījums nosūtīts!",
        message: "Paldies par interesi par KlinikTrack. Mūsu komanda izskatīs jūsu pieprasījumu un drīzumā sazināsies.",
        goBack: "Atpakaļ uz sākumu"
      }
    },
    countries: {
      germany: "Vācija",
      austria: "Austrija",
      switzerland: "Šveice",
      luxembourg: "Luksemburga",
      liechtenstein: "Lihtenšteina",
      otherEurope: "Cita Eiropa"
    },
    departments: {
      operations: "Operācijas",
      logistics: "Loģistika",
      biomedical: "Biomedicīna",
      it: "IT",
      qualityCompliance: "Kvalitāte un atbilstība",
      nursing: "Māsu aprūpe",
      procurement: "Iepirkumi",
      other: "Cits"
    },
    facilityTypes: {
      publicHospital: "Valsts slimnīca",
      privateClinic: "Privātklīnika",
      universityHospital: "Universitātes slimnīca",
      rehabLongterm: "Rehabilitācija un ilgtermiņa aprūpe",
      other: "Cits"
    },
    hospitalSizes: {
      under200: "< 200 gultas",
      size200to500: "200 – 500 gultas",
      size500to1000: "500 – 1 000 gultas",
      over1000: "1 000+ gultas"
    },
    equipment: {
      infusion_pumps: "Infūzijas sūkņi",
      beds: "Gultas",
      wheelchairs: "Ratiņkrēsli",
      biomedical_mobile: "Mobilais biomedicīnas aprīkojums",
      carts: "Rati",
      cables_accessories: "Kabeļi un piederumi",
      medication_boxes: "Medikamentu kastes"
    },
    timeframes: {
      days0to30: "0 – 30 dienas",
      months1to3: "1 – 3 mēneši",
      months3to6: "3 – 6 mēneši",
      over6months: "> 6 mēneši",
      exploring: "Tikai izpētu"
    },
    referralSources: {
      referral: "Ieteikums",
      linkedin: "LinkedIn",
      event: "Pasākums",
      google: "Google",
      partner: "Partneris",
      other: "Cits"
    },
    faq: {
      title: "Biežāk uzdotie jautājumi",
      categories: {
        dataPrivacy: "Dati un privātums",
        technical: "Tehniskais un instalācija",
        staffPatients: "Personāls un pacienti",
        buyingProcess: "Pirkšanas process",
        scope: "Produkta apjoms"
      },
      data1q: "Kādus datus KlinikTrack vāc?",
      data1a: "Tikai aprīkojuma atrašanās vietas datus (pēc zonas/stāva), statusu un izmantošanas metadatus. Pacientu dati netiek vākti.",
      data2q: "Kur dati tiek glabāti?",
      data2a: "Dati tiek glabāti Eiropas datu centros, kas atbilst GDPR (Vācija un Šveice).",
      data3q: "Vai dati ir šifrēti?",
      data3a: "Jā. Dati tranzītā un miera stāvoklī ir šifrēti saskaņā ar pašreizējiem standartiem.",
      data4q: "Kas var piekļūt datiem?",
      data4a: "Tikai autorizēti slimnīcas lietotāji, izmantojot uz lomām balstītu piekļuves kontroli. KlinikTrack piekļūst datiem tikai ar tiešu atļauju atbalsta nolūkos.",
      tech1q: "Kāda tehnoloģija tiek izmantota?",
      tech1a: "KlinikTrack izmanto bezvadu tehnoloģiju kombināciju (BLE un WiFi) atkarībā no slimnīcas vides.",
      tech2q: "Vai KlinikTrack nepieciešama IT integrācija?",
      tech2a: "Nē. KlinikTrack darbojas uz neatkarīgas infrastruktūras un neprasa integrāciju ar klīniskajām sistēmām.",
      inst1q: "Kā tagi tiek piestiprināti?",
      inst1a: "Tagi ir mazas ierīces, kas tiek montētas vai pievienotas aprīkojumam. Instalācija ir ātra un neprasa aprīkojuma modifikācijas.",
      inst2q: "Vai operāciju zāle jāslēdz instalācijai?",
      inst2a: "Nē. Tagi tiek ātri pievienoti mobilajam aprīkojumam bez dīkstāves vai telpu slēgšanas.",
      inst3q: "Cik ilgi aizņem instalācija?",
      inst3a: "Tipiskas pilotinstalācijas aizņem dažas stundas atkarībā no aprīkojuma skaita. Izvietošana var notikt pa nodaļām.",
      staff1q: "Vai personāls jutīsies uzraudzīts?",
      staff1a: "Nē. KlinikTrack izseko aprīkojumu, nevis cilvēkus. Nav personāla izsekošanas, snieguma uzraudzības vai individuālas identifikācijas.",
      staff2q: "Vai pacienti pamanīs sistēmu?",
      staff2a: "Vairumā gadījumu nē. Ierīces ir diskrētas un uzstādītas uz aprīkojuma, nevis pacientiem vai telpās.",
      staff3q: "Vai personālam nepieciešama apmācība?",
      staff3a: "Nepieciešama minimāla apmācība. Vadības panelis ir intuitīvs, un lielākā daļa lietotāju var sākt mazāk nekā 15 minūtēs.",
      buy1q: "Kas parasti izmanto KlinikTrack?",
      buy1a: "Loģistikas un operāciju komandas, biomedicīnas nodaļas, ēku pārvaldība un kvalitātes un atbilstības komandas.",
      buy2q: "Kas parasti pieņem lēmumu par KlinikTrack iegādi?",
      buy2a: "Slimnīcas vadība un operāciju vadītāji ar ieguldījumu no iepirkumu, IT un biomedicīnas komandām.",
      buy3q: "Kādu problēmu KlinikTrack atrisina vispirms?",
      buy3a: "Tas samazina laiku, kas zaudēts, meklējot aprīkojumu, un nodrošina skaidru redzamību par aktīvu pieejamību.",
      buy4q: "Vai KlinikTrack ir medicīniska ierīce?",
      buy4a: "Nē. KlinikTrack nav klasificēts kā medicīniska ierīce un neatbalsta klīniskus lēmumus.",
      scope1q: "Ko dara KlinikTrack?",
      scope1a: "Tas nodrošina operatīvo redzamību mobilajiem aktīviem, atbalsta loģistiku un plānošanu, un palīdz samazināt meklēšanas laiku un nevajadzīgus pirkumus.",
      scope2q: "Ko KlinikTrack nedara?",
      scope2a: "Tas neizseko pacientus vai personālu, neaizstāj klīniskās sistēmas un neautomatizē medicīniskus lēmumus."
    },
    footer: {
      solutions: "Risinājumi",
      assetTracking: "Aktīvu izsekošana (KlinikTrack)",
      elderaCare: "Eldera Care",
      healthMonitoring: "Veselības uzraudzība",
      mobileCare: "Mobilā aprūpe",
      allSolutions: "Visi risinājumi",
      company: "Uzņēmums",
      legal: "Juridiskā informācija",
      privacyPolicy: "Privātuma politika",
      gdpr: "GDPR",
      terms: "Noteikumi",
      imprint: "Iespiedums",
      copyright: "© 2026 KlinikTrack. Visas tiesības aizsargātas. Operē 0g Holding SA."
    },
    privacy: {
      title: "Privātuma politika (GDPR)",
      lastUpdated: "Pēdējo reizi atjaunināts"
    }
  },
  
  // Lithuanian translations
  lt: {
    nav: {
      requestDemo: "Prašyti demo",
      backToHome: "Grįžti į pradžią"
    },
    hero: {
      title: "KlinikTrack",
      subtitle: "Ligoninės įrangos sekimas — Supaprastintas",
      tagline1: "Plug & play matomumas kritinei medicinos įrangai.",
      tagline2: "Sukurta ligoninės operacijoms.",
      cta: "Prašyti prieigos prie demo platformos"
    },
    problem: {
      title: "Ligoninės praranda laiką — ne įrangą.",
      description: "Kiekvieną dieną ligoninės personalas švaisto brangų laiką ieškodamas mobilios įrangos. Turtas juda tarp skyrių be matomumo, sukeldamas neefektyvumą ir operatyvinį spaudimą.",
      bullet1: "Įrangą sunku rasti tarp aukštų ir skyrių",
      bullet2: "Naudojimas blogai suprantamas",
      bullet3: "\"Visam atvejui\" pirkimai tampa norma",
      bullet4: "Operatyvinis stresas didėja"
    },
    solution: {
      title: "Operatyvinis matomumas be trikdžių.",
      description: "KlinikTrack suteikia ligoninėms paprastą būdą rasti ir stebėti kritinę įrangą pagal zoną, aukštą ar skyrių — neliečiant klinikinių darbo eigų ar pacientų duomenų.",
      feature1Title: "Turto matomumas pagal vietą",
      feature1Desc: "Žinokite, kur yra jūsų įranga bet kuriuo metu.",
      feature2Title: "Paprasti įspėjimai ir būsenos stebėjimas",
      feature2Desc: "Gaukite pranešimus, kai pasikeičia įrangos būsena.",
      feature3Title: "Naudojimo ir prieinamumo įžvalgos",
      feature3Desc: "Supraskite, kaip naudojamas jūsų turtas.",
      closing: "Sukurta operacijoms. Projektuota ligoninėms."
    },
    track: {
      title: "Ką galite sekti su KlinikTrack?",
      description: "KlinikTrack orientuojasi į mobilų, didelės vertės ir dažnai pamestą ligoninės turtą, kuris yra kritiškai svarbus kasdienėms operacijoms.",
      items: {
        infusionPumps: "Infuziniai siurbliai",
        beds: "Ligoninės lovos",
        wheelchairs: "Neįgaliųjų vežimėliai",
        biomedical: "Mobili biomedicininė įranga",
        carts: "Priežiūros ir aptarnavimo vežimėliai",
        cables: "Kabeliai ir priedai",
        medicationBoxes: "Vaistų dėžutės"
      },
      disclaimer: "KlinikTrack neseka pacientų, personalo ar fiksuoto turto."
    },
    why: {
      title: "Kodėl KlinikTrack?",
      features: {
        simpleTitle: "Paprastas ir neįkyrus",
        simpleDesc: "Nėra integracijos su klinikinėmis sistemomis ar darbo eigos trikdžių.",
        realTimeTitle: "Realaus laiko duomenys",
        realTimeDesc: "Momentinis įrangos vietos ir būsenos matomumas.",
        privacyTitle: "Privatumui draugiškas",
        privacyDesc: "KlinikTrack nerenka ir neapdoroja pacientų duomenų.",
        scaleTitle: "Sukurtas augimui",
        scaleDesc: "Nuo mažų padalinių iki didelių daugiavietų ligoninių tinklų.",
        roiTitle: "Aiškus ROI",
        roiDesc: "Sumažinkite paieškos laiką, pagerinkite naudojimą ir venkite nereikalingų pirkimų."
      }
    },
    preview: {
      title: "Platformos peržiūra",
      description: "Paprasta, intuityvi sąsaja įrangos radimui ir valdymui realiu laiku."
    },
    audience: {
      title: "Sukurta ligoninės operacijoms.",
      groups: {
        management: "Ligoninės vadovybė",
        logistics: "Logistikos ir operacijų komandos",
        biomedical: "Biomedicinos skyriai",
        quality: "Kokybė ir atitiktis"
      }
    },
    form: {
      title: "Prisijungti prie KlinikTrack demo platformos",
      subtitle: "Užpildykite informaciją žemiau, kad prašytumėte prieigos.",
      sections: {
        yourInfo: "Jūsų informacija",
        hospitalInfo: "Ligoninės informacija",
        whatToTrack: "Ką norite sekti?",
        timeline: "Projekto laikotarpis",
        additional: "Papildoma informacija"
      },
      fields: {
        fullName: "Pilnas vardas",
        email: "El. paštas",
        organization: "Organizacija / Ligoninių grupė",
        role: "Pareigos",
        country: "Šalis",
        department: "Skyrius",
        phone: "Telefonas",
        facilityType: "Įstaigos tipas",
        hospitalSize: "Ligoninės dydis (lovų)",
        otherEquipment: "Kita įranga",
        targetTimeframe: "Tikslinis pradžios laikotarpis",
        referralSource: "Kaip sužinojote apie KlinikTrack?",
        comments: "Komentarai"
      },
      placeholders: {
        fullName: "Jūsų pilnas vardas",
        email: "jusu@elpastas.lt",
        organization: "Ligoninės ar grupės pavadinimas",
        role: "Pvz.: Logistikos vadovas",
        department: "Pasirinkite skyrių",
        phone: "Telefono numeris (neprivaloma)",
        facilityType: "Pasirinkite įstaigos tipą",
        hospitalSize: "Pasirinkite dydį",
        otherEquipment: "Kita įranga, kurią norite sekti",
        timeframe: "Pasirinkite laikotarpį",
        referral: "Pasirinkite šaltinį",
        comments: "Specifiniai klausimai ar reikalavimai?"
      },
      gdprConsent: "Sutinku, kad mano informacija būtų apdorota atsakant į mano užklausą ir suteikiant demo prieigą.",
      privacyPolicy: "Privatumo politika",
      submit: "Prašyti demo prieigos",
      submitting: "Siunčiama...",
      reviewNote: "Prieiga peržiūrima siekiant užtikrinti aktualumą.",
      pleaseSpecify: "(prašome patikslinti)",
      optional: "(neprivaloma)",
      success: {
        title: "Užklausa išsiųsta!",
        message: "Dėkojame už susidomėjimą KlinikTrack. Mūsų komanda peržiūrės jūsų užklausą ir netrukus susisieks.",
        goBack: "Grįžti į pradžią"
      }
    },
    countries: {
      germany: "Vokietija",
      austria: "Austrija",
      switzerland: "Šveicarija",
      luxembourg: "Liuksemburgas",
      liechtenstein: "Lichtenšteinas",
      otherEurope: "Kita Europa"
    },
    departments: {
      operations: "Operacijos",
      logistics: "Logistika",
      biomedical: "Biomedicina",
      it: "IT",
      qualityCompliance: "Kokybė ir atitiktis",
      nursing: "Slauga",
      procurement: "Pirkimai",
      other: "Kita"
    },
    facilityTypes: {
      publicHospital: "Valstybinė ligoninė",
      privateClinic: "Privati klinika",
      universityHospital: "Universitetinė ligoninė",
      rehabLongterm: "Reabilitacija ir ilgalaikė priežiūra",
      other: "Kita"
    },
    hospitalSizes: {
      under200: "< 200 lovų",
      size200to500: "200 – 500 lovų",
      size500to1000: "500 – 1 000 lovų",
      over1000: "1 000+ lovų"
    },
    equipment: {
      infusion_pumps: "Infuziniai siurbliai",
      beds: "Lovos",
      wheelchairs: "Neįgaliųjų vežimėliai",
      biomedical_mobile: "Mobili biomedicininė įranga",
      carts: "Vežimėliai",
      cables_accessories: "Kabeliai ir priedai",
      medication_boxes: "Vaistų dėžutės"
    },
    timeframes: {
      days0to30: "0 – 30 dienų",
      months1to3: "1 – 3 mėnesiai",
      months3to6: "3 – 6 mėnesiai",
      over6months: "> 6 mėnesiai",
      exploring: "Tik tyrinėju"
    },
    referralSources: {
      referral: "Rekomendacija",
      linkedin: "LinkedIn",
      event: "Renginys",
      google: "Google",
      partner: "Partneris",
      other: "Kita"
    },
    faq: {
      title: "Dažnai užduodami klausimai",
      categories: {
        dataPrivacy: "Duomenys ir privatumas",
        technical: "Techniniai ir įdiegimas",
        staffPatients: "Personalas ir pacientai",
        buyingProcess: "Pirkimo procesas",
        scope: "Produkto apimtis"
      },
      data1q: "Kokius duomenis renka KlinikTrack?",
      data1a: "Tik įrangos vietos duomenis (pagal zoną/aukštą), būseną ir naudojimo metaduomenis. Pacientų duomenys nerenkami.",
      data2q: "Kur saugomi duomenys?",
      data2a: "Duomenys saugomi BDAR atitinkančiuose Europos duomenų centruose (Vokietija ir Šveicarija).",
      data3q: "Ar duomenys užšifruoti?",
      data3a: "Taip. Duomenys persiuntimo ir ramybės būsenoje yra užšifruoti pagal dabartinius standartus.",
      data4q: "Kas gali pasiekti duomenis?",
      data4a: "Tik autorizuoti ligoninės vartotojai per rolėmis pagrįstą prieigos kontrolę. KlinikTrack pasiekia duomenis tik su aiškiu leidimu palaikymo tikslais.",
      tech1q: "Kokia technologija naudojama?",
      tech1a: "KlinikTrack naudoja belaidžių technologijų derinį (BLE ir WiFi) priklausomai nuo ligoninės aplinkos.",
      tech2q: "Ar KlinikTrack reikalinga IT integracija?",
      tech2a: "Ne. KlinikTrack veikia nepriklausomoje infrastruktūroje ir nereikalauja integracijos su klinikinėmis sistemomis.",
      inst1q: "Kaip tvirtinami žymekliai?",
      inst1a: "Žymekliai yra maži įrenginiai, montuojami ar tvirtinami prie įrangos. Įdiegimas greitas ir nereikalauja įrangos modifikacijų.",
      inst2q: "Ar operacinė turi būti uždaryta diegimui?",
      inst2a: "Ne. Žymekliai greitai tvirtinami prie mobilios įrangos be prastovų ar patalpų uždarymo.",
      inst3q: "Kiek laiko trunka įdiegimas?",
      inst3a: "Tipiniai bandomieji diegimai trunka kelias valandas priklausomai nuo įrangos kiekio. Diegimas gali vykti pagal skyrius.",
      staff1q: "Ar personalas jausis stebimas?",
      staff1a: "Ne. KlinikTrack seka įrangą, ne žmones. Nėra personalo sekimo, našumo stebėjimo ar individualios identifikacijos.",
      staff2q: "Ar pacientai pastebės sistemą?",
      staff2a: "Daugeliu atvejų ne. Įrenginiai yra diskretūs ir montuojami ant įrangos, ne ant pacientų ar patalpose.",
      staff3q: "Ar personalui reikia mokymo?",
      staff3a: "Reikalingas minimalus mokymas. Valdymo skydas yra intuityvus, ir dauguma vartotojų gali pradėti per mažiau nei 15 minučių.",
      buy1q: "Kas paprastai naudoja KlinikTrack?",
      buy1a: "Logistikos ir operacijų komandos, biomedicinos skyriai, pastatų valdymas ir kokybės bei atitikties komandos.",
      buy2q: "Kas paprastai priima sprendimą pirkti KlinikTrack?",
      buy2a: "Ligoninės vadovybė ir operacijų vadovai su indėliu iš pirkimų, IT ir biomedicinos komandų.",
      buy3q: "Kokią problemą KlinikTrack išsprendžia pirmiausia?",
      buy3a: "Jis sumažina laiką, prarastą ieškant įrangos, ir suteikia aiškų turto prieinamumo matomumą.",
      buy4q: "Ar KlinikTrack yra medicinos prietaisas?",
      buy4a: "Ne. KlinikTrack nėra klasifikuojamas kaip medicinos prietaisas ir nepalaiko klinikinių sprendimų.",
      scope1q: "Ką daro KlinikTrack?",
      scope1a: "Jis suteikia operatyvinį matomumą mobiliajam turtui, palaiko logistiką ir planavimą, ir padeda sumažinti paieškos laiką bei nereikalingus pirkimus.",
      scope2q: "Ko KlinikTrack nedaro?",
      scope2a: "Jis neseka pacientų ar personalo, nepakeičia klinikinių sistemų ir neautomatizuoja medicininių sprendimų."
    },
    footer: {
      solutions: "Sprendimai",
      assetTracking: "Turto sekimas (KlinikTrack)",
      elderaCare: "Eldera Care",
      healthMonitoring: "Sveikatos stebėjimas",
      mobileCare: "Mobili priežiūra",
      allSolutions: "Visi sprendimai",
      company: "Įmonė",
      legal: "Teisinė informacija",
      privacyPolicy: "Privatumo politika",
      gdpr: "BDAR",
      terms: "Sąlygos",
      imprint: "Leidėjo informacija",
      copyright: "© 2026 KlinikTrack. Visos teisės saugomos. Operuoja 0g Holding SA."
    },
    privacy: {
      title: "Privatumo politika (BDAR)",
      lastUpdated: "Paskutinį kartą atnaujinta"
    }
  }
};

export default translations;
